/**
 * LibrarySidebar — displays the list of accessible libraries,
 * an expandable page tree for the selected library, and a search input.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePagesContext } from '../../state/pages-context';
import { PageTreeNode } from './PageTreeNode';
import { SearchResults } from './SearchResults';
import { SemanticSearch } from './SemanticSearch';
import { LibraryShareDialog } from './LibraryShareDialog';
import { Button } from '../../components/ui/button';
import * as pagesApi from '../../api/pages-api';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LibrarySidebar(): React.ReactElement {
  const navigate = useNavigate();
  const { libraryId: activeLibraryId, pageId: activePageId } = useParams<{
    libraryId?: string;
    pageId?: string;
  }>();
  const { state, fetchLibraries, selectLibrary, fetchPageTree, createPage } = usePagesContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [semanticMode, setSemanticMode] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showNewLibraryInput, setShowNewLibraryInput] = useState(false);
  const [newLibraryName, setNewLibraryName] = useState('');
  const [newLibraryError, setNewLibraryError] = useState<string | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());

  // Fetch libraries and favorites on mount
  useEffect(() => {
    fetchLibraries();
    pagesApi.getFavoriteLibraries().then((ids) => setFavoriteIds(new Set(ids))).catch(() => {});
  }, [fetchLibraries]);

  // Sort libraries: favorites first, then alphabetical
  const sortedLibraries = useMemo(() => {
    return [...state.libraries].sort((a, b) => {
      const aFav = favoriteIds.has(a.libraryId);
      const bFav = favoriteIds.has(b.libraryId);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [state.libraries, favoriteIds]);

  const toggleFavorite = useCallback(async (libraryId: string) => {
    const newFavorites = new Set(favoriteIds);
    if (newFavorites.has(libraryId)) {
      newFavorites.delete(libraryId);
    } else {
      newFavorites.add(libraryId);
    }
    setFavoriteIds(newFavorites);
    await pagesApi.putFavoriteLibraries([...newFavorites]).catch(() => {});
  }, [favoriteIds]);

  // Debounced search
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchQuery(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        setActiveSearchQuery(value.trim());
      }, 300);
    },
    []
  );

  const handleLibraryClick = useCallback(
    (libraryId: string) => {
      selectLibrary(libraryId);
      fetchPageTree(libraryId);
      navigate(`/pages/${libraryId}`);
    },
    [selectLibrary, fetchPageTree, navigate]
  );

  const handleNewPage = useCallback(
    async (parentPageId?: string | null) => {
      if (!state.selectedLibraryId) return;
      const pageId = await createPage(state.selectedLibraryId, {
        title: 'Untitled Page',
        parentPageId: parentPageId ?? null,
      });
      navigate(`/pages/${state.selectedLibraryId}/${pageId}`);
    },
    [state.selectedLibraryId, createPage, navigate]
  );

  const handleNewLibrarySubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newLibraryName.trim()) return;
      setNewLibraryError(null);
      try {
        const { createLibrary } = await import('../../api/pages-api');
        const lib = await createLibrary({ name: newLibraryName.trim() });
        setNewLibraryName('');
        setNewLibraryError(null);
        setShowNewLibraryInput(false);
        await fetchLibraries();
        handleLibraryClick(lib.libraryId);
      } catch (err) {
        // A 409 means the (case-insensitive) library name is already taken.
        const status = (err as { statusCode?: number })?.statusCode;
        if (status === 409) {
          setNewLibraryError('A library with this name already exists. Library names must be unique.');
        } else {
          const message = (err as { message?: string })?.message;
          setNewLibraryError(message ? `Could not create library: ${message}` : 'Could not create library. Please try again.');
        }
      }
    },
    [newLibraryName, fetchLibraries, handleLibraryClick]
  );

  // Filter tree nodes by search query
  const filteredTree = searchQuery.trim()
    ? filterTree(state.pageTree, searchQuery.toLowerCase())
    : state.pageTree;

  return (
    <div className="flex flex-col h-full">
      {/* Search input */}
      <div className="p-3 border-b">
        <div className="flex items-center gap-1.5">
          <label htmlFor="pages-search" className="sr-only">
            Search pages
          </label>
          <input
            id="pages-search"
            type="search"
            placeholder={semanticMode ? 'Ask a question...' : 'Search pages...'}
            value={searchQuery}
            onChange={handleSearchChange}
            className="flex-1 px-3 py-1.5 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={semanticMode ? 'Semantic search' : 'Search pages'}
          />
          <button
            onClick={() => setSemanticMode(!semanticMode)}
            className={`shrink-0 px-2 py-1.5 text-xs rounded-md border transition-colors ${
              semanticMode
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-muted-foreground border-input hover:bg-accent'
            }`}
            title={semanticMode ? 'Switch to text search' : 'Switch to semantic search'}
            aria-pressed={semanticMode}
          >
            AI
          </button>
        </div>
      </div>

      {/* Libraries list */}
      <nav className="flex-1 overflow-y-auto p-2" aria-label="Libraries">
        {/* Show search results when search is active */}
        {activeSearchQuery && !semanticMode && (
          <SearchResults
            query={activeSearchQuery}
            onClose={() => {
              setSearchQuery('');
              setActiveSearchQuery('');
            }}
          />
        )}

        {activeSearchQuery && semanticMode && (
          <SemanticSearch
            query={activeSearchQuery}
            libraryId={state.selectedLibraryId ?? undefined}
            onClose={() => {
              setSearchQuery('');
              setActiveSearchQuery('');
            }}
          />
        )}

        {!activeSearchQuery && state.libraries.length === 0 && !state.loading && (
          <p className="px-2 py-4 text-sm text-muted-foreground text-center">
            No libraries yet. Create one to get started.
          </p>
        )}

        {!activeSearchQuery && (
        <ul className="space-y-1" role="list">
          {sortedLibraries.map((library) => {
            const isSelected = library.libraryId === activeLibraryId;
            const isFavorite = favoriteIds.has(library.libraryId);
            return (
              <li key={library.libraryId}>
                <div className="flex items-center group">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(library.libraryId); }}
                    className={`w-5 h-5 flex items-center justify-center shrink-0 rounded transition-colors ${
                      isFavorite
                        ? 'text-yellow-500'
                        : 'text-transparent group-hover:text-muted-foreground hover:!text-yellow-500'
                    }`}
                    aria-label={isFavorite ? `Remove ${library.name} from favorites` : `Add ${library.name} to favorites`}
                    title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
                      <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleLibraryClick(library.libraryId)}
                    className={`flex-1 text-left px-2 py-2 text-sm rounded-md transition-colors ${
                      isSelected
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'text-foreground hover:bg-accent/50'
                    }`}
                    aria-current={isSelected ? 'true' : undefined}
                  >
                    {library.name}
                  </button>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity mr-1">
                    <LibraryShareDialog
                      libraryId={library.libraryId}
                      libraryName={library.name}
                      currentVisibility={library.visibility}
                      trigger={
                        <button
                          className="w-5 h-5 flex items-center justify-center shrink-0 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                          aria-label={`Access & Sharing for ${library.name}`}
                          title="Access & Sharing"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
                            <path d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z" />
                          </svg>
                        </button>
                      }
                    />
                  </div>
                </div>

                {/* Page tree for selected library */}
                {isSelected && (
                  <div className="ml-2 mt-1">
                    {filteredTree.length > 0 ? (
                      <ul role="tree" aria-label={`Pages in ${library.name}`}>
                        {filteredTree.map((node, idx) => (
                          <PageTreeNode
                            key={node.pageId}
                            node={node}
                            depth={0}
                            activePageId={activePageId ?? null}
                            libraryId={library.libraryId}
                            onCreatePage={handleNewPage}
                            siblings={filteredTree}
                            siblingIndex={idx}
                          />
                        ))}
                      </ul>
                    ) : (
                      !state.loading && (
                        <p className="px-2 py-2 text-xs text-muted-foreground">
                          No pages yet.
                        </p>
                      )
                    )}

                    {/* New page button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full mt-1 text-xs justify-start"
                      onClick={() => handleNewPage(null)}
                    >
                      + New Page
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        )}
      </nav>

      {/* New Library section */}
      <div className="p-3 border-t">
        {showNewLibraryInput ? (
          <form onSubmit={handleNewLibrarySubmit} className="flex flex-col gap-1">
            <div className="flex gap-1">
            <label htmlFor="new-library-name" className="sr-only">
              Library name
            </label>
            <input
              id="new-library-name"
              type="text"
              value={newLibraryName}
              onChange={(e) => {
                setNewLibraryName(e.target.value);
                if (newLibraryError) setNewLibraryError(null);
              }}
              placeholder="Library name"
              aria-invalid={newLibraryError ? true : undefined}
              aria-describedby={newLibraryError ? 'new-library-error' : undefined}
              className="flex-1 px-2 py-1 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
            <Button type="submit" size="sm" variant="default">
              Add
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowNewLibraryInput(false);
                setNewLibraryName('');
                setNewLibraryError(null);
              }}
            >
              ✕
            </Button>
            </div>
            {newLibraryError && (
              <p id="new-library-error" role="alert" className="px-1 text-xs text-destructive">
                {newLibraryError}
              </p>
            )}
          </form>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-full text-sm"
            onClick={() => setShowNewLibraryInput(true)}
          >
            + New Library
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-sm text-muted-foreground mt-1"
          onClick={() => navigate('/pages/concepts')}
        >
          Browse Concepts
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tree filter utility
// ---------------------------------------------------------------------------

function filterTree(
  nodes: Array<{ pageId: string; title: string; parentPageId: string | null; sortOrder: string; children: Array<unknown> }>,
  query: string
): Array<{ pageId: string; title: string; parentPageId: string | null; sortOrder: string; children: Array<unknown> }> {
  const results: Array<{ pageId: string; title: string; parentPageId: string | null; sortOrder: string; children: Array<unknown> }> = [];
  for (const node of nodes) {
    const typedChildren = node.children as typeof nodes;
    const filteredChildren = filterTree(typedChildren, query);
    if (node.title.toLowerCase().includes(query) || filteredChildren.length > 0) {
      results.push({ ...node, children: filteredChildren });
    }
  }
  return results;
}
